import { useAbility } from '@casl/react';
import { ChevronRight, ExpandMore, Map } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { AbilityContext, Can } from 'src/context/canContext';
import { SidebarContext } from 'src/context/SidebarContext';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import { useApplications } from 'src/hooks/useApplications';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useSelectedApplicationGroupId } from 'src/hooks/useSelectedApplicationGroupId';
import { useSelectedApplicationId } from 'src/hooks/useSelectedApplicationId';
import {
  isApiKeysPath,
  isApplicationGroupDetailPath,
  isApplicationsPath,
  isScopedDashboardPath,
  isUsersPath,
} from 'src/router/pathUtils';
import { ALL_SELECTION_ID, isAllSelection } from 'src/utils/selection';
import Scrollbar from 'switch-fe-shared/components/Scrollbar/Scrollbar';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';
import { ApplicationGroup } from 'switch-shared/modules/crud/application-group/application-group.entity';
import { Application } from 'switch-shared/modules/crud/application/application.entity';

import pages from '../../../router/routes';
import AvatarDropdown from './AvatarDropdown';
import CollapsibleMenuItem from './CollapsibleMenuItem';
import {
  APPLICATION_SIDEBAR_SECTION_STORAGE_KEY,
  ApplicationSidebarSectionId,
  SIDEBAR_SECTION_STORAGE_KEY,
  SidebarSectionId,
  getInitialExpandedApplicationSidebarSections,
  getInitialExpandedSidebarSections,
  toggleExpandedApplicationSidebarSection,
  toggleExpandedSidebarSection,
} from './sidebarSectionStorage';

interface CollapsibleSidebarMenuProps {
  isCollapsed: boolean;
}

const MenuWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(
  ({ theme, collapsed }) => `
  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: bold;
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.sidebar.menuItemHeadingColor};
    padding: ${theme.spacing(0, 2.5)};
    line-height: 1.4;
    opacity: ${collapsed ? '0' : '1'};
    transition: opacity 0.2s ease-in-out;
  }

  .MuiButton-root {
    height: 38px;
    transition: none;
    border-radius: ${collapsed ? theme.tokens.borderRadius.full : theme.tokens.borderRadius.none} !important;
    font-size: ${theme.typography.pxToRem(12)};

    & > .MuiButton-label {
      font-family: ${theme.tokens.typography.fontFamily.display};
    }
  }
`
);

const SubMenuWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity',
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

interface SidebarSectionProps {
  children: ReactNode;
  isCollapsed: boolean;
  isEnabled: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  title: string;
}

const SectionHeader = styled(ListItemButton)(
  ({ theme }) => `
    min-height: 44px;
    padding: ${theme.spacing(0.75, 2.25)};
    color: ${theme.sidebar.menuItemHeadingColor};
    border-radius: ${theme.shape.borderRadius}px;

    .MuiListItemText-primary {
      font-size: ${theme.typography.pxToRem(11)};
      font-weight: 700;
      letter-spacing: 0;
      text-transform: uppercase;
      line-height: 1.3;
    }

    .MuiSvgIcon-root {
      font-size: ${theme.typography.pxToRem(18)};
    }
  `
);

const SidebarSection = ({
  children,
  isCollapsed,
  isEnabled,
  isExpanded,
  onToggle,
  title,
}: SidebarSectionProps) => {
  if (isCollapsed || !isEnabled) {
    return (
      <SubMenuWrapper collapsed={isCollapsed}>
        <List component='div'>{children}</List>
      </SubMenuWrapper>
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      <SectionHeader
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
        onClick={onToggle}
      >
        <ListItemText primary={title} />
        {isExpanded ? <ExpandMore /> : <ChevronRight />}
      </SectionHeader>
      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        <SubMenuWrapper collapsed={isCollapsed}>
          <List component='div' disablePadding>
            {children}
          </List>
        </SubMenuWrapper>
      </Collapse>
    </Box>
  );
};

interface ApplicationMenuGroupProps {
  children: ReactNode;
  isExpanded: boolean;
  isCollapsed: boolean;
  isEnabled: boolean;
  onToggle: () => void;
  title: string;
}

const ApplicationMenuGroup = ({
  children,
  isExpanded,
  isCollapsed,
  isEnabled,
  onToggle,
  title,
}: ApplicationMenuGroupProps) => {
  if (isCollapsed || !isEnabled) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ pt: 0.5 }}>
      <SectionHeader
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
        onClick={onToggle}
        sx={{
          minHeight: 36,
          pl: 2.75,
          pr: 1.25,
          py: 0.25,
          '& .MuiListItemText-primary': {
            fontSize: 10,
          },
          '& .MuiSvgIcon-root': {
            fontSize: 16,
          },
        }}
      >
        <ListItemText primary={title} />
        {isExpanded ? <ExpandMore /> : <ChevronRight />}
      </SectionHeader>
      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        {children}
      </Collapse>
    </Box>
  );
};

const CollapsibleSidebarMenu = ({
  isCollapsed,
}: CollapsibleSidebarMenuProps) => {
  /* Hooks & Use Effects */
  const { closeSidebar } = useContext(SidebarContext);
  const ability = useAbility(AbilityContext);
  const isSuperAdmin = ability.can(ACTIONS.MANAGE, 'all');
  const theme = useTheme();
  const selectedGroupId = useEffectiveApplicationGroupId();
  const selectedAppId = useEffectiveApplicationId();
  const {
    applicationGroups,
    isLoading: isGroupsLoading,
    isFetching: isGroupsFetching,
  } = useApplicationGroups();
  const {
    applications,
    isLoading: isAppsLoading,
    isFetching: isAppsFetching,
  } = useApplications();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isUrlNavigationEnabled,
    neutralDashboardPath,
    getApplicationGroupPath,
    getApplicationGroupDashboardPath,
    getApplicationGroupsPath,
    getApplicationPath,
    getApplicationDashboardPath,
    getApplicationsPath,
    getApplicationScopedPath,
    getGroupScopedPath,
    getUsersPath,
    getApiKeysPath,
    getGroupChangePath,
    getApplicationChangePath,
  } = useAdminUrlNavigation();
  const { setSelectedGroupId } = useSelectedApplicationGroupId();
  const { setSelectedApplicationId } = useSelectedApplicationId();
  const isAllGroupsSelected = isAllSelection(selectedGroupId);
  const applicationGroupsPath = getApplicationGroupsPath();
  const dashboardPath = !isAllSelection(selectedAppId)
    ? getApplicationDashboardPath(selectedAppId, selectedGroupId)
    : !isAllGroupsSelected
      ? getApplicationGroupDashboardPath(selectedGroupId)
      : neutralDashboardPath;
  const accountingPath = pages.accounting.path;
  const reportsPath = getGroupScopedPath(
    'reports',
    pages.reports.path,
    selectedGroupId
  );
  const tariffsPath = getGroupScopedPath(
    'tariffs',
    pages.tariffs.path,
    selectedGroupId
  );
  const providersPath = pages.providers.path;
  const unallocatedDevicesPath = pages.unallocatedDevices.path;
  const integrationsPath = getGroupScopedPath(
    'integrations',
    pages.integrations.path,
    selectedGroupId
  );
  const accountsPath = getApplicationScopedPath(
    'accounts',
    pages.accounts.path,
    selectedGroupId,
    selectedAppId
  );
  const utilitiesPath = getApplicationScopedPath(
    'utilities',
    pages.utility.path,
    selectedGroupId,
    selectedAppId
  );
  const meterPointsPath = getApplicationScopedPath(
    pages.meterpoints.name,
    pages.meterpoints.path,
    selectedGroupId,
    selectedAppId
  );
  const metersPath = getApplicationScopedPath(
    'meters',
    pages.meters.path,
    selectedGroupId,
    selectedAppId
  );
  const gatewaysPath = getApplicationScopedPath(
    'gateways',
    pages.gateways.path,
    selectedGroupId,
    selectedAppId
  );
  const sensorsPath = getApplicationScopedPath(
    'sensors',
    pages.sensors.path,
    selectedGroupId,
    selectedAppId
  );
  const zonesPath = getApplicationScopedPath(
    'zones',
    pages.zones.path,
    selectedGroupId,
    selectedAppId
  );
  const manualReadingsPath = getApplicationScopedPath(
    'manual-data',
    pages.manualReadings.path,
    selectedGroupId,
    selectedAppId
  );
  const paymentsPath = getApplicationScopedPath(
    'payments',
    pages.payments.path,
    selectedGroupId,
    selectedAppId
  );
  const invoicesPath = getApplicationScopedPath(
    'invoices',
    pages.invoices.path,
    selectedGroupId,
    selectedAppId
  );
  const reconsPath = getApplicationScopedPath(
    'recons',
    pages.recons.path,
    selectedGroupId,
    selectedAppId
  );
  const mapPath = getApplicationScopedPath(
    'map',
    pages.map.path,
    selectedGroupId,
    selectedAppId
  );
  const emailIntegrationLogsPath = getApplicationScopedPath(
    'email-integration-logs',
    pages.emailIntegrationLogs.path,
    selectedGroupId,
    selectedAppId
  );

  const isUtilityDataEditingDisabled = useFeatureFlag(
    'ac_disable_utility_data_editing__ops'
  );
  const disableUtilityDataEditing =
    isUtilityDataEditingDisabled && !isSuperAdmin;
  const showRecon = useFeatureFlag('ac_reconciliation_page__release');
  const isApiKeysEnabled = useFeatureFlag('ac_api_keys__release');
  const showInvoices = useFeatureFlag('ac_invoicing_overhaul__release');
  const isGroupedSidebarNavigationEnabled = useFeatureFlag(
    'ac_grouped_sidebar_navigation__release'
  );

  /* States */
  const [expandedSectionIds, setExpandedSectionIds] = useState<
    SidebarSectionId[]
  >(() =>
    getInitialExpandedSidebarSections(
      localStorage.getItem(SIDEBAR_SECTION_STORAGE_KEY)
    )
  );
  const [expandedApplicationSectionIds, setExpandedApplicationSectionIds] =
    useState<ApplicationSidebarSectionId[]>(() =>
      getInitialExpandedApplicationSidebarSections(
        localStorage.getItem(APPLICATION_SIDEBAR_SECTION_STORAGE_KEY)
      )
    );

  /* API Calls */

  /* Functions & Functional Components */
  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_SECTION_STORAGE_KEY,
      JSON.stringify(expandedSectionIds)
    );
  }, [expandedSectionIds]);

  useEffect(() => {
    localStorage.setItem(
      APPLICATION_SIDEBAR_SECTION_STORAGE_KEY,
      JSON.stringify(expandedApplicationSectionIds)
    );
  }, [expandedApplicationSectionIds]);

  const isSectionExpanded = (sectionId: SidebarSectionId) => {
    return expandedSectionIds.includes(sectionId);
  };

  const toggleSection = (sectionId: SidebarSectionId) => {
    setExpandedSectionIds((currentSectionIds) =>
      toggleExpandedSidebarSection(currentSectionIds, sectionId)
    );
  };

  const isApplicationSectionExpanded = (
    sectionId: ApplicationSidebarSectionId
  ) => {
    return expandedApplicationSectionIds.includes(sectionId);
  };

  const toggleApplicationSection = (sectionId: ApplicationSidebarSectionId) => {
    setExpandedApplicationSectionIds((currentSectionIds) =>
      toggleExpandedApplicationSidebarSection(currentSectionIds, sectionId)
    );
  };

  const allGroupsOption = useMemo(
    () => ({
      _id: ALL_SELECTION_ID,
      name: 'All Groups',
    }),
    []
  );

  const allApplicationsOption = useMemo(
    () => ({
      _id: ALL_SELECTION_ID,
      name: 'All Applications',
    }),
    []
  );

  const availableApplicationGroups = useMemo(
    () =>
      (applicationGroups ?? []).filter(
        (group) => !isAllSelection(group._id) && group.name !== 'All Groups'
      ),
    [applicationGroups]
  );

  const groupDropdownOptions = useMemo(() => {
    return availableApplicationGroups.length > 1
      ? [allGroupsOption, ...availableApplicationGroups]
      : availableApplicationGroups;
  }, [allGroupsOption, availableApplicationGroups]);

  const groupDropdownValue = isGroupsLoading
    ? { _id: '', name: '' }
    : isAllSelection(selectedGroupId) && availableApplicationGroups.length > 1
      ? allGroupsOption
      : availableApplicationGroups.find(
          (element: ApplicationGroup) => element._id === selectedGroupId
        ) || { _id: '', name: '' };
  const isGroupSelectorLoading =
    isUrlNavigationEnabled &&
    (isGroupsLoading || isGroupsFetching) &&
    !groupDropdownValue.name;

  const appDropdownOptions = useMemo(() => {
    const apps = applications ?? [];

    const hasAllOption = apps.some((app) => isAllSelection(app._id));
    return hasAllOption ? apps : [allApplicationsOption, ...apps];
  }, [allApplicationsOption, applications]);

  const appDropdownValue = isAppsLoading
    ? { _id: '', name: '' }
    : isAllSelection(selectedAppId)
      ? allApplicationsOption
      : (applications ?? []).find(
          (element: Application) => element._id === selectedAppId
        ) || allApplicationsOption;
  const isAppSelectorLoading =
    isUrlNavigationEnabled &&
    !isAllGroupsSelected &&
    (isAppsLoading || isAppsFetching);
  const showPlatformSection = isSuperAdmin;

  return (
    <>
      <MenuWrapper
        collapsed={isCollapsed}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <SubMenuWrapper collapsed={isCollapsed}>
          <List component='div'>
            <CollapsibleMenuItem
              to={dashboardPath}
              icon={<EntityIcon type='dashboard' />}
              label='Dashboard'
              isCollapsed={isCollapsed}
              onClick={closeSidebar}
            />
          </List>
        </SubMenuWrapper>

        <Scrollbar style={{ width: '100%', flex: 1 }} autoHide>
          {showPlatformSection && (
            <>
              <SidebarSection
                title='Platform'
                isCollapsed={isCollapsed}
                isEnabled={isGroupedSidebarNavigationEnabled}
                isExpanded={isSectionExpanded('platform')}
                onToggle={() => toggleSection('platform')}
              >
                {availableApplicationGroups.length > 1 && (
                  <>
                    <Can I={ACTIONS.MANAGE} a='all'>
                      <CollapsibleMenuItem
                        to={applicationGroupsPath}
                        icon={<EntityIcon type='applicationGroups' />}
                        label='Application Groups'
                        isCollapsed={isCollapsed}
                        onClick={closeSidebar}
                        activeWhen={(pathname) =>
                          pathname === applicationGroupsPath ||
                          pathname === pages.applicationGroup.path ||
                          pathname.startsWith(
                            `${pages.applicationGroup.path}/`
                          ) ||
                          isApplicationGroupDetailPath(pathname)
                        }
                      />
                    </Can>
                  </>
                )}
                <Can I={ACTIONS.MANAGE} a='all'>
                  <CollapsibleMenuItem
                    to={accountingPath}
                    icon={<EntityIcon type='accounting' />}
                    label='Accounting'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
                <Can I={ACTIONS.MANAGE} a='all'>
                  <CollapsibleMenuItem
                    to={providersPath}
                    icon={<EntityIcon type='providers' />}
                    label='Providers'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
                <Can I={ACTIONS.MANAGE} a='all'>
                  <CollapsibleMenuItem
                    to={unallocatedDevicesPath}
                    icon={<EntityIcon type='unallocatedDevices' />}
                    label='Unallocated Devices'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
              </SidebarSection>

              <Divider
                sx={{
                  display: isCollapsed ? 'none' : 'block',
                  mx: theme.spacing(2),
                  my: theme.spacing(1),
                  background: theme.colors.alpha.trueWhite[10],
                }}
              />
            </>
          )}
          <SidebarSection
            title='Group'
            isCollapsed={isCollapsed}
            isEnabled={isGroupedSidebarNavigationEnabled}
            isExpanded={isSectionExpanded('group')}
            onToggle={() => toggleSection('group')}
          >
            {(availableApplicationGroups.length > 1 ||
              isGroupSelectorLoading) && (
              <AvatarDropdown
                options={groupDropdownOptions}
                value={groupDropdownValue}
                loading={isGroupSelectorLoading}
                label='Group'
                getOptionLabel={(option: any) => option.name}
                onChange={(_e: any, newValue: any) => {
                  if (!newValue?._id) return;
                  setSelectedGroupId(newValue._id);
                  navigate(getGroupChangePath(newValue._id, location.pathname));
                }}
                isCollapsed={isCollapsed}
                avatarColor={theme.colors.primary.main}
                enableCopyId={true}
                ability={ability}
                openPath={getApplicationGroupPath}
                canOpenAction={ACTIONS.READ}
                canOpenSubject='ApplicationGroups'
              />
            )}
            {!isAllGroupsSelected ? (
              <>
                <Can I={ACTIONS.READ} a='Applications'>
                  <CollapsibleMenuItem
                    to={getApplicationsPath(selectedGroupId)}
                    icon={<EntityIcon type='applications' />}
                    label='Applications'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                    activeWhen={(pathname) =>
                      !isScopedDashboardPath(pathname) &&
                      (pathname === pages.application.path ||
                        pathname.startsWith(`${pages.application.path}/`) ||
                        isApplicationsPath(pathname))
                    }
                  />
                </Can>
                <Can I={ACTIONS.MANAGE} a='Users'>
                  <CollapsibleMenuItem
                    to={getUsersPath(selectedGroupId)}
                    icon={<EntityIcon type='users' />}
                    label='Users'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                    activeWhen={(pathname) =>
                      pathname === pages.users.path ||
                      pathname.startsWith(`${pages.users.path}/`) ||
                      isUsersPath(pathname)
                    }
                  />
                </Can>
                {isApiKeysEnabled && (
                  <Can I={ACTIONS.MANAGE} a='all'>
                    <CollapsibleMenuItem
                      to={getApiKeysPath(selectedGroupId)}
                      icon={<EntityIcon type='apiKeys' />}
                      label='API Keys'
                      isCollapsed={isCollapsed}
                      onClick={closeSidebar}
                      activeWhen={(pathname) =>
                        pathname === pages.apiKeys.path ||
                        pathname.startsWith(`${pages.apiKeys.path}/`) ||
                        isApiKeysPath(pathname)
                      }
                    />
                  </Can>
                )}
                <Can I={ACTIONS.READ} a='Reports'>
                  <CollapsibleMenuItem
                    to={reportsPath}
                    icon={<EntityIcon type='reporting' />}
                    label='Reporting'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
                <Can I={ACTIONS.READ} a='Tariffs'>
                  <CollapsibleMenuItem
                    to={tariffsPath}
                    icon={<EntityIcon type='tariffs' />}
                    label='Tariffs'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
                <Can I={ACTIONS.MANAGE} a='Applications'>
                  <CollapsibleMenuItem
                    to={integrationsPath}
                    icon={<EntityIcon type='integrations' />}
                    label='Integrations'
                    isCollapsed={isCollapsed}
                    onClick={closeSidebar}
                  />
                </Can>
              </>
            ) : (
              <Typography
                variant='body1'
                color={theme.tokens.colors.mutedForeground}
                sx={{
                  fontSize: theme.tokens.typography.fontSize.xs,
                  textAlign: 'center',
                  px: `${theme.tokens.spacing.md}px`,
                  py: `${theme.tokens.spacing.md}px`,
                  opacity: isCollapsed ? 0 : 1,
                }}
              >
                Select a group to view its details
              </Typography>
            )}
          </SidebarSection>

          {!isAllGroupsSelected && (
            <>
              <Divider
                sx={{
                  display: isCollapsed ? 'none' : 'block',
                  mx: theme.spacing(2),
                  my: theme.spacing(1),
                  background: theme.colors.alpha.trueWhite[10],
                }}
              />
              <SidebarSection
                title='Application'
                isCollapsed={isCollapsed}
                isEnabled={isGroupedSidebarNavigationEnabled}
                isExpanded={isSectionExpanded('application')}
                onToggle={() => toggleSection('application')}
              >
                <AvatarDropdown
                  options={appDropdownOptions}
                  value={appDropdownValue}
                  loading={isAppSelectorLoading}
                  disabled={isAppSelectorLoading}
                  disabledTooltip='Loading applications...'
                  label='Application'
                  getOptionLabel={(option: any) => option.name}
                  onChange={(_e: any, newValue: any) => {
                    if (!newValue?._id) return;
                    setSelectedApplicationId(newValue._id);
                    navigate(
                      getApplicationChangePath(
                        newValue._id,
                        selectedGroupId,
                        location.pathname
                      )
                    );
                  }}
                  isCollapsed={isCollapsed}
                  avatarColor={theme.colors.primary.main}
                  enableCopyId={true}
                  ability={ability}
                  openPath={(id) => getApplicationPath(id, selectedGroupId)}
                  canOpenAction={ACTIONS.READ}
                  canOpenSubject='Applications'
                />
                {!isAllSelection(selectedAppId) &&
                  selectedAppId !== undefined && (
                    <>
                      <ApplicationMenuGroup
                        title='Manage'
                        isCollapsed={isCollapsed}
                        isEnabled={isGroupedSidebarNavigationEnabled}
                        isExpanded={isApplicationSectionExpanded(
                          'accounts-and-utilities'
                        )}
                        onToggle={() =>
                          toggleApplicationSection('accounts-and-utilities')
                        }
                      >
                        <Can I={ACTIONS.READ} a='Accounts'>
                          <CollapsibleMenuItem
                            to={accountsPath}
                            icon={<EntityIcon type='accounts' />}
                            label='Accounts'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        <Can I={ACTIONS.READ} a='ApplicationUtilities'>
                          <CollapsibleMenuItem
                            to={utilitiesPath}
                            icon={<EntityIcon type='utilities' />}
                            label='Utilities'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                            disabled={
                              !(
                                !isAllSelection(selectedAppId) &&
                                selectedAppId !== undefined
                              )
                            }
                          />
                        </Can>
                      </ApplicationMenuGroup>

                      <ApplicationMenuGroup
                        title='Operations'
                        isCollapsed={isCollapsed}
                        isEnabled={isGroupedSidebarNavigationEnabled}
                        isExpanded={isApplicationSectionExpanded('operations')}
                        onToggle={() => toggleApplicationSection('operations')}
                      >
                        <Can I={ACTIONS.READ} a='MeterPoints'>
                          <CollapsibleMenuItem
                            to={meterPointsPath}
                            icon={<EntityIcon type='meterPoints' />}
                            label='Meter Points'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>

                        <Can I={ACTIONS.READ} a='Meters'>
                          <CollapsibleMenuItem
                            to={metersPath}
                            icon={<EntityIcon type='meters' />}
                            label='Meters'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        <Can I={ACTIONS.READ} a='Gateways'>
                          <CollapsibleMenuItem
                            to={gatewaysPath}
                            icon={<EntityIcon type='gateways' />}
                            label='Gateways'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        <Can I={ACTIONS.READ} a='Sensors'>
                          <CollapsibleMenuItem
                            to={sensorsPath}
                            icon={<EntityIcon type='sensors' />}
                            label='Sensors'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        <Can I={ACTIONS.READ} a='Zones'>
                          <CollapsibleMenuItem
                            to={zonesPath}
                            icon={<EntityIcon type='zones' />}
                            label='Zones'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        {!disableUtilityDataEditing && (
                          <Can I={ACTIONS.MANAGE} a='UtilityData'>
                            <CollapsibleMenuItem
                              to={manualReadingsPath}
                              icon={<EntityIcon type='manualReadings' />}
                              label='Manual Readings'
                              isCollapsed={isCollapsed}
                              onClick={closeSidebar}
                            />
                          </Can>
                        )}
                      </ApplicationMenuGroup>

                      <ApplicationMenuGroup
                        title='Finance'
                        isCollapsed={isCollapsed}
                        isEnabled={isGroupedSidebarNavigationEnabled}
                        isExpanded={isApplicationSectionExpanded('finance')}
                        onToggle={() => toggleApplicationSection('finance')}
                      >
                        <Can I={ACTIONS.READ} a='Payments'>
                          <CollapsibleMenuItem
                            to={paymentsPath}
                            icon={<EntityIcon type='payments' />}
                            label='Payments'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        {showInvoices && (
                          <Can I={ACTIONS.READ} a='Invoices'>
                            <CollapsibleMenuItem
                              to={invoicesPath}
                              icon={<EntityIcon type='invoices' />}
                              label='Invoices'
                              isCollapsed={isCollapsed}
                              onClick={closeSidebar}
                            />
                          </Can>
                        )}
                        {showRecon && (
                          <Can I={ACTIONS.MANAGE} a='all'>
                            <CollapsibleMenuItem
                              to={reconsPath}
                              icon={<EntityIcon type='reconciliations' />}
                              label='Reconciliations'
                              isCollapsed={isCollapsed}
                              onClick={closeSidebar}
                            />
                          </Can>
                        )}
                      </ApplicationMenuGroup>

                      <ApplicationMenuGroup
                        title='Insights'
                        isCollapsed={isCollapsed}
                        isEnabled={isGroupedSidebarNavigationEnabled}
                        isExpanded={isApplicationSectionExpanded('insights')}
                        onToggle={() => toggleApplicationSection('insights')}
                      >
                        <Can I={ACTIONS.READ} a='Meters'>
                          <CollapsibleMenuItem
                            to={mapPath}
                            icon={<Map />}
                            label='Map'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                        <Can I={ACTIONS.READ} a='all'>
                          <CollapsibleMenuItem
                            to={emailIntegrationLogsPath}
                            icon={<EntityIcon type='emailIntegrationLogs' />}
                            label='Email Integration Logs'
                            isCollapsed={isCollapsed}
                            onClick={closeSidebar}
                          />
                        </Can>
                      </ApplicationMenuGroup>
                    </>
                  )}
                {(selectedAppId === undefined ||
                  isAllSelection(selectedAppId)) && (
                  <Typography
                    variant='body1'
                    color={theme.tokens.colors.mutedForeground}
                    sx={{
                      fontSize: theme.tokens.typography.fontSize.xs,
                      textAlign: 'center',
                      px: `${theme.tokens.spacing.md}px`,
                      py: `${theme.tokens.spacing.md}px`,
                      opacity: isCollapsed ? 0 : 1,
                    }}
                  >
                    Select an application to view its details
                  </Typography>
                )}
              </SidebarSection>
            </>
          )}
        </Scrollbar>
      </MenuWrapper>
    </>
  );
};

export default CollapsibleSidebarMenu;
